import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import { FormLabel } from '../../Form';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { Dropdown } from '../../elements/Dropdown';
import Button from '../../elements/Button';
import { toast } from 'react-toastify';
import {
  CompanyResponse,
  inviteLearnersToCohort,
  LEARNER_STATUS,
} from '../../../api/admin';
import { ScrollableTooltip } from '../../Tooltip';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';

const ErrorText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textDanger};
`;

const ErrorLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textDanger};
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  /* identical to box height, or 133% */
  font-feature-settings: 'tnum' on, 'lnum' on;
  transition: all 300ms ease-in-out;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;
type LearnerSplitType = {
  learnersInCohort: CompanyResponse['learners'][number][];
  learnersToRestart: CompanyResponse['learners'][number][];
  learnersToInvite: CompanyResponse['learners'][number][];
  learnersWithoutModuleInLearningPlan: CompanyResponse['learners'][number][];
};

const getInviteButtonState = ({
  slackData,
  learnersInCohort,
  nextOpenCohort,
  selectedModule,
  learnersWithoutModuleInLearningPlan,
}: {
  learnersInCohort: CompanyResponse['learners'][number][];
  learnersWithoutModuleInLearningPlan: CompanyResponse['learners'][number][];
  slackData: CompanyResponse['slack'] | undefined;
  nextOpenCohort: CompanyResponse['cohorts'][number] | undefined;
  selectedModule: CompanyResponse['modules'][number] | null;
}) => {
  if (!nextOpenCohort) {
    return {
      canInvite: false,
      tooltip: 'There are no open cohorts. Create one first',
    };
  }

  if (!slackData?.statusChannel?.id || !slackData?.discussChannel?.id) {
    return {
      canInvite: false,
      tooltip: 'This company has no #status or #discuss channels configured',
    };
  }

  if (!selectedModule) {
    return {
      canInvite: false,
      tooltip: 'Please select a module first',
    };
  }

  if (learnersWithoutModuleInLearningPlan.length > 0) {
    return {
      canInvite: false,
      tooltip:
        'There are learners without the selected module in their learning plan. Please add it first.',
    };
  }

  if (learnersInCohort.length > 0) {
    return {
      canInvite: false,
      tooltip:
        "There are learners with invites or progress in the selected cohort and module. You can't invite them again.",
    };
  }

  if (!selectedModule.hasOnboardingEmail) {
    return {
      canInvite: false,
      tooltip: "This module doesn't have a custom onboarding email setup",
    };
  }

  if (!selectedModule.hasOnboardingSurvey) {
    return {
      canInvite: false,
      tooltip: "This module doesn't have an onboarding survey",
    };
  }

  return {
    canInvite: true,
    tooltip: undefined,
  };
};

export default function InviteLearnersToCohortModal({
  isOpen,
  onClose,
  companyId,
  selectedLearnerIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  selectedLearnerIds: number[];
}) {
  const {
    data,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetCompany(companyId);

  const nextOpenCohort = data?.cohorts.find((c) => c.isOpen);
  const moduleOptions = data?.modules
    .filter((m) => !m.isDeprecated && !m.isDraft)
    .map((m) => ({
      label: m.contentTitle,
      value: m.contentId,
    }));
  const [selectedModule, setSelectedModule] =
    React.useState<{
      label: string;
      value: number;
    } | null>(null);

  const { getTokenSilently } = useAuth();

  const {
    learnersInCohort,
    learnersToRestart,
    learnersToInvite,
    learnersWithoutModuleInLearningPlan,
  } = selectedLearnerIds.reduce<LearnerSplitType>(
    (acc, learnerId) => {
      const learner = data?.learners.find((l) => l.id === learnerId);
      if (!learner) {
        throw new Error(`Learner with id ${learnerId} not found`);
      }

      if (!selectedModule) {
        return acc;
      }

      const learningPlanItem = learner.learningPlan?.find(
        (l) => l.type === 'module' && l.content.id === selectedModule.value
      );

      if (!learningPlanItem) {
        acc.learnersWithoutModuleInLearningPlan.push(learner);
        return acc;
      }

      const hasProgressInSelectedCohortAndModule =
        nextOpenCohort?.learners.find(
          (l) =>
            l?.learnerId === learnerId &&
            l?.module.contentId === selectedModule?.value
        );

      if (hasProgressInSelectedCohortAndModule) {
        acc.learnersInCohort.push(learner);
        return acc;
      }

      const hasProgressInSelectedModule = data?.cohorts
        .map((c) => c.learners)
        .flat()
        .find(
          (l) =>
            l?.learnerId === learnerId &&
            l?.module.contentId === selectedModule?.value &&
            [LEARNER_STATUS.ONBOARDED, LEARNER_STATUS.RESTARTED].includes(
              l?.status
            )
        );

      if (hasProgressInSelectedModule) {
        acc.learnersToRestart.push(learner);
        return acc;
      }

      acc.learnersToInvite.push(learner);
      return acc;
    },
    {
      learnersInCohort: [],
      learnersToRestart: [],
      learnersToInvite: [],
      learnersWithoutModuleInLearningPlan: [],
    } as LearnerSplitType
  );

  const startDateFromNow = dayjs.utc(nextOpenCohort?.startDate).fromNow();
  const isStartDateInThePast = dayjs
    .utc(nextOpenCohort?.startDate)
    .isBefore(dayjs.utc());

  const { canInvite, tooltip: buttonDisableTooltip } = getInviteButtonState({
    learnersInCohort,
    learnersWithoutModuleInLearningPlan,
    slackData: data?.slack,
    nextOpenCohort,
    selectedModule:
      data?.modules.find((m) => m.contentId === selectedModule?.value) ?? null,
  });

  const cohortValue = nextOpenCohort
    ? {
        label: `${dayjs
          .utc(nextOpenCohort.startDate)
          .format('MMM D, YYYY')} - ${dayjs
          .utc(nextOpenCohort.endDate)
          .format('MMM D, YYYY')}`,
        value: nextOpenCohort.cycleId,
      }
    : null;

  const inviteLearnersMutation = useMutation({
    mutationFn: async () => {
      if (!nextOpenCohort?.cycleId) {
        throw new Error('There is no open cohort. Create one first.');
      }
      if (!selectedModule?.value) {
        throw new Error('Please select a module first.');
      }

      if (selectedLearnerIds.length === 0) {
        throw new Error('Please select at least one learner.');
      }

      const token = await getTokenSilently();
      return inviteLearnersToCohort(token, {
        companyId,
        cohortId: nextOpenCohort?.cycleId,
        learnerIds: selectedLearnerIds,
        contentId: selectedModule?.value,
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Learners invited successfully');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to invite learners'
      );
    },
  });

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <Row>
        <Header>Invite Learners to Cohort</Header>
        <CloseButton onClick={onClose} />
      </Row>
      <FormLabel>Next Open Cohort</FormLabel>
      {nextOpenCohort ? (
        <Dropdown
          options={[cohortValue]}
          value={cohortValue}
          isDisabled
          menuPortalTarget={document.body}
        />
      ) : (
        <ErrorText>
          No open cohort found. Create one{' '}
          <ErrorLink
            to={`/admin/companies/${companyId}/cohorts`}
            target="_blank"
          >
            here
          </ErrorLink>
          .
        </ErrorText>
      )}

      <FormLabel>Module</FormLabel>
      <Dropdown
        options={moduleOptions}
        onChange={setSelectedModule}
        value={selectedModule}
        placeholder="Select a module"
        isMulti={false}
        isClearable
        isValidNewOption={() => false}
        menuPortalTarget={document.body}
      />
      <PSmall style={{ marginTop: '32px' }}>
        You are about to invite {selectedLearnerIds.length} learners to the
        cohort.
      </PSmall>

      {learnersInCohort.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-in-cohort">
              {learnersInCohort.length} learners
            </Bold>{' '}
            with progress or invites in {selectedModule?.label}. You can't
            invite them again.
          </PSmall>
          <EmailsTooltip
            emails={learnersInCohort.map((l) => `${l.name} (${l.email})`)}
            buttonId={'learners-in-cohort'}
          />
        </>
      )}
      {learnersWithoutModuleInLearningPlan.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-without-module">
              {learnersWithoutModuleInLearningPlan.length} learners
            </Bold>{' '}
            without {selectedModule?.label} in their learning plan. Please
            update their learning plan first.
          </PSmall>
          <EmailsTooltip
            emails={learnersWithoutModuleInLearningPlan.map(
              (l) => `${l.name} (${l.email})`
            )}
            buttonId={'learners-without-module'}
          />
        </>
      )}
      {learnersToRestart.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-to-restart">
              {learnersToRestart.length} learners
            </Bold>{' '}
            that already have progress in {selectedModule?.label}. They will be
            "restarted", meaning they won't be invited to onboard, but pick up
            where they left off in the previous cycle.
          </PSmall>
          <EmailsTooltip
            emails={learnersToRestart.map((l) => `${l.name} (${l.email})`)}
            buttonId={'learners-to-restart'}
          />
        </>
      )}

      {learnersToInvite.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-to-invite">
              {learnersToInvite.length} learners
            </Bold>{' '}
            that will be invited to the cohort. An email onboarding sequence
            will be scheduled starting 1 week before the cohort start date. The
            next cohort {isStartDateInThePast ? 'started' : 'starts'}{' '}
            {startDateFromNow}.
            {isStartDateInThePast &&
              'This means some emails might be sent out as soon as you click "Invite".'}
          </PSmall>
          <EmailsTooltip
            emails={learnersToInvite.map((l) => `${l.name} (${l.email})`)}
            buttonId={'learners-to-invite'}
          />
        </>
      )}
      <Row>
        <Button
          label="Invite Learners"
          showIconBeforeLabel
          faIcon="fa-solid fa-paper-plane"
          type="primary"
          onClick={() => {
            inviteLearnersMutation.mutate();
          }}
          isDisabled={!canInvite || inviteLearnersMutation.isLoading}
          isLoading={inviteLearnersMutation.isLoading || isLoadingCompany}
          tooltip={buttonDisableTooltip}
        />
      </Row>
    </Wrapper>
  );
}

const EmailsTooltip = ({
  emails,
  buttonId,
}: {
  emails: string[];
  buttonId: string;
}) => {
  return (
    <ScrollableTooltip
      anchorSelect={`.${buttonId}`}
      place="top"
      delayHide={500}
    >
      <div style={{ overflowY: 'auto' }}>
        <span
          style={{
            whiteSpace: 'pre-wrap',
            overflowY: 'auto',
          }}
        >
          {emails.join('\n')}
        </span>
      </div>
    </ScrollableTooltip>
  );
};

const Bold = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
`;
